import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent, type IconName } from '../../icons/icon.component';

export type AlertType = 'success' | 'error' | 'warning' | 'info';

@Component({
  selector: 'app-alert',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './alert.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: block;
      }

      .alert-progress-bar {
        animation: progress linear forwards;
      }

      @keyframes progress {
        from {
          width: 100%;
        }
        to {
          width: 0%;
        }
      }

      p {
        white-space: pre-wrap;
      }
    `,
  ],
})
export class AlertComponent implements OnInit, OnDestroy {
  @Input() message: string = '';
  @Input() type: AlertType = 'error';
  @Input() duration: number = 5000;
  @Input() closable: boolean = true;
  @Input() showProgress: boolean = true;

  @Output() close = new EventEmitter<void>();

  isVisible = signal(false);
  isExiting = signal(false);

  private alertTimer?: ReturnType<typeof setTimeout>;
  private visibilityTimer?: ReturnType<typeof setTimeout>;
  private readonly exitAnimationDurationMs = 300;

  private readonly typeConfig: Record<
    AlertType,
    { icon: IconName; baseClasses: string; progressColor: string }
  > = {
    error: {
      icon: 'circleAlertIcon',
      baseClasses:
        'bg-red-50 border border-red-200 text-red-700 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-300',
      progressColor: 'bg-red-500 dark:bg-red-400',
    },
    success: {
      icon: 'check',
      baseClasses:
        'bg-emerald-50 border border-emerald-200 text-emerald-700 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-300',
      progressColor: 'bg-emerald-500 dark:bg-emerald-400',
    },
    warning: {
      icon: 'triangleAlert',
      baseClasses:
        'bg-amber-50 border border-amber-200 text-amber-700 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-300',
      progressColor: 'bg-amber-500 dark:bg-amber-400',
    },
    info: {
      icon: 'info',
      baseClasses:
        'bg-blue-50 border border-blue-200 text-blue-700 dark:bg-blue-500/10 dark:border-blue-500/30 dark:text-blue-300',
      progressColor: 'bg-blue-500 dark:bg-blue-400',
    },
  };

  ngOnInit(): void {
    this.visibilityTimer = setTimeout(() => {
      this.isVisible.set(true);
    }, 10);

    if (this.duration > 0) {
      this.alertTimer = setTimeout(() => {
        this.handleClose();
      }, this.duration);
    }
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  handleClose(): void {
    if (this.alertTimer) {
      clearTimeout(this.alertTimer);
    }

    this.isExiting.set(true);
    setTimeout(() => {
      this.close.emit();
    }, this.exitAnimationDurationMs);
  }

  private clearTimers(): void {
    if (this.alertTimer) {
      clearTimeout(this.alertTimer);
    }
    if (this.visibilityTimer) {
      clearTimeout(this.visibilityTimer);
    }
  }

  getAlertClasses(): string {
    const config = this.typeConfig[this.type];
    const baseClasses = `mb-6 p-4 rounded-lg flex items-center gap-3 transition-all duration-300 ease-in-out relative overflow-hidden ${config.baseClasses}`;

    let animationClasses = 'opacity-0 translate-y-[-10px]';
    if (this.isVisible()) {
      animationClasses = 'opacity-100 translate-y-0';
    }
    if (this.isExiting()) {
      animationClasses = 'opacity-0 translate-y-[-10px]';
    }

    return `${baseClasses} ${animationClasses}`;
  }

  getIconName(): IconName {
    return this.typeConfig[this.type].icon;
  }

  getProgressColor(): string {
    return this.typeConfig[this.type].progressColor;
  }

  getCloseButtonClasses(): string {
    const typeColors: Record<AlertType, string> = {
      error: 'text-red-400 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/20 dark:hover:text-red-300',
      success: 'text-emerald-400 hover:bg-emerald-100 hover:text-emerald-700 dark:hover:bg-emerald-500/20 dark:hover:text-emerald-300',
      warning: 'text-amber-400 hover:bg-amber-100 hover:text-amber-700 dark:hover:bg-amber-500/20 dark:hover:text-amber-300',
      info: 'text-blue-400 hover:bg-blue-100 hover:text-blue-700 dark:hover:bg-blue-500/20 dark:hover:text-blue-300',
    };

    return `shrink-0 w-6 h-6 flex items-center justify-center border-none bg-transparent cursor-pointer rounded-sm transition-colors duration-200 ${typeColors[this.type]}`;
  }
}
